/**
 * Centralized error tracking & performance monitoring utility.
 * Captures errors, slow queries, and user-facing issues.
 * Logs are buffered in memory and periodically flushed to the backend.
 */

import { supabase } from "@/integrations/supabase/client";

type LogLevel = "info" | "warn" | "error" | "perf";

interface LogEntry {
  level: LogLevel;
  message: string;
  context?: Record<string, unknown>;
  timestamp: string;
  userId?: string;
  route?: string;
}

const LOG_BUFFER: LogEntry[] = [];
const MAX_BUFFER = 100;
const FLUSH_INTERVAL_MS = 60_000; // 1 minute
const FLUSH_THRESHOLD = 10; // flush when buffer hits this size

let flushTimer: ReturnType<typeof setInterval> | null = null;

function getRoute(): string {
  try {
    return window.location.pathname;
  } catch {
    return "unknown";
  }
}

function createEntry(
  level: LogLevel,
  message: string,
  context?: Record<string, unknown>
): LogEntry {
  return {
    level,
    message,
    context,
    timestamp: new Date().toISOString(),
    route: getRoute(),
  };
}

function pushEntry(entry: LogEntry) {
  LOG_BUFFER.push(entry);
  if (LOG_BUFFER.length > MAX_BUFFER) LOG_BUFFER.shift();

  // Console output in development
  if (import.meta.env.DEV) {
    const fn = entry.level === "error" ? console.error
      : entry.level === "warn" ? console.warn
      : console.log;
    fn(`[${entry.level.toUpperCase()}] ${entry.message}`, entry.context ?? "");
  }

  // Auto-flush on threshold or immediately for errors
  if (entry.level === "error" || LOG_BUFFER.length >= FLUSH_THRESHOLD) {
    flushLogs();
  }
}

/** Flush buffered logs to backend */
export async function flushLogs(): Promise<void> {
  if (LOG_BUFFER.length === 0) return;

  const logsToSend = LOG_BUFFER.splice(0, LOG_BUFFER.length);

  try {
    const payload = logsToSend.map((l) => ({
      level: l.level,
      message: l.message,
      context: JSON.parse(JSON.stringify(l.context ?? {})),
      route: l.route ?? null,
    }));

    const { error } = await (supabase.rpc as any)("insert_monitoring_logs", {
      p_logs: payload,
    });

    if (error) {
      // Put logs back if flush failed (don't lose them)
      if (import.meta.env.DEV) {
        console.warn("[Monitoring] Flush failed:", error.message);
      }
      LOG_BUFFER.unshift(...logsToSend);
    }
  } catch {
    // Silently fail — put logs back
    LOG_BUFFER.unshift(...logsToSend);
  }
}

/** Start periodic flush timer */
export function startMonitoring() {
  if (flushTimer) return;
  flushTimer = setInterval(flushLogs, FLUSH_INTERVAL_MS);

  // Flush on page unload — use sendBeacon for reliable delivery
  if (typeof window !== "undefined") {
    window.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        flushViaSendBeacon();
      }
    });

    // Also try pagehide for iOS Safari
    window.addEventListener("pagehide", flushViaSendBeacon);
  }
}

/**
 * Flush logs using navigator.sendBeacon for reliable delivery
 * when the page is being unloaded or hidden.
 * Falls back to regular flushLogs if sendBeacon is unavailable.
 */
function flushViaSendBeacon(): void {
  if (LOG_BUFFER.length === 0) return;

  if (typeof navigator.sendBeacon !== "function") {
    flushLogs();
    return;
  }

  const logsToSend = LOG_BUFFER.splice(0, LOG_BUFFER.length);
  const payload = logsToSend.map((l) => ({
    level: l.level,
    message: l.message,
    context: JSON.parse(JSON.stringify(l.context ?? {})),
    route: l.route ?? null,
  }));

  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
    if (!supabaseUrl || !anonKey) {
      LOG_BUFFER.unshift(...logsToSend);
      return;
    }

    // Try to get the current session token for authenticated logging
    let bearerToken = anonKey;
    try {
      const stored = localStorage.getItem("sb-turcmcoemxyxmlnndiqu-auth-token");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.access_token) bearerToken = parsed.access_token;
      }
    } catch { /* fallback to anon key */ }

    const url = `${supabaseUrl}/rest/v1/rpc/insert_monitoring_logs`;

    // Use keepalive fetch (sendBeacon doesn't support custom headers)
    const sent = fetch(url, {
      method: "POST",
      headers: {
        apikey: anonKey,
        Authorization: `Bearer ${bearerToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ p_logs: payload }),
      keepalive: true,
    }).catch(() => {
      // Silently fail — page is closing
    });

    // Don't await — fire and forget
    void sent;
  } catch {
    LOG_BUFFER.unshift(...logsToSend);
  }
}

/** Log an informational message */
export function logInfo(message: string, context?: Record<string, unknown>) {
  pushEntry(createEntry("info", message, context));
}

/** Log a warning */
export function logWarn(message: string, context?: Record<string, unknown>) {
  pushEntry(createEntry("warn", message, context));
}

/** Log an error with optional Error object */
export function logError(
  message: string,
  error?: unknown,
  context?: Record<string, unknown>
) {
  const errorDetails = error instanceof Error
    ? { name: error.name, message: error.message, stack: error.stack?.slice(0, 500) }
    : { raw: String(error) };

  pushEntry(createEntry("error", message, { ...context, error: errorDetails }));
}

/**
 * Measure and log execution time of an async operation.
 * Logs a warning if it exceeds the threshold.
 */
export async function measurePerf<T>(
  label: string,
  fn: () => Promise<T>,
  warnThresholdMs = 2000
): Promise<T> {
  const start = performance.now();
  try {
    const result = await fn();
    const duration = Math.round(performance.now() - start);

    if (duration > warnThresholdMs) {
      pushEntry(createEntry("perf", `Slow operation: ${label}`, {
        duration_ms: duration,
        threshold_ms: warnThresholdMs,
      }));
    }

    return result;
  } catch (error) {
    const duration = Math.round(performance.now() - start);
    logError(`Failed operation: ${label}`, error, { duration_ms: duration });
    throw error;
  }
}

/** Get buffered logs (useful for debug panels) */
export function getLogBuffer(): readonly LogEntry[] {
  return LOG_BUFFER;
}

/** Clear the log buffer */
export function clearLogBuffer() {
  LOG_BUFFER.length = 0;
}

/** Global error handler — attach to window.onerror in main.tsx */
export function globalErrorHandler(event: ErrorEvent) {
  logError("Unhandled error", event.error, {
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
}

export function globalRejectionHandler(event: PromiseRejectionEvent) {
  logError("Unhandled promise rejection", event.reason);
}
